import { FormArray } from '@angular/forms';
import { GenericLeg, QuotePair } from '@approot/models';
import {
  getReferenceCounterNotional,
  getReferenceNotional
} from '@approot/services/rfq/rfq-tab/rfq-tab';
import { dvSchemas, Utilities } from '@approot/utilities';
import { components } from '@approot/utilities/digital-vega-schema';
const { cleanFormattedNumeric } = Utilities;

export interface BookedHedge {
  hedgeAmount: number;
  hedgeRate: number;
}

interface NonVolBasedPremiumValues {
  premiumValue: number;
  margin: number;
  clientPremium: number;
  cashPremium?: number;
}

export function nonVolBasedPremiumStylesPremiums(values: {
  productType: string;
  adjustedQuote: dvSchemas['ClientQuoteEvent']['quote'];
  originalQuote: dvSchemas['ClientQuoteEvent']['quote'];
  isBid: boolean;
  leg: components['schemas']['ClientLegDTO'];
  index: number;
  quoteLeg: components['schemas']['ClientLegDTO'];
  currencyPair: QuotePair;
  tradeBookingPremiumCurrency: string;
  tradeBookingPremiumStyle: string;
  legConversionRate: number;
  forwardPointsMultiplier: number;
  referenceNotional: number;
  referenceCounterNotional: number;
}): NonVolBasedPremiumValues {
  const isInverted = !values.isBid ? values.leg.inverted : !values.leg.inverted;
  const premiumValue = !isInverted
    ? values.originalQuote.legs[values.index].premiumAmountOffer
    : values.originalQuote.legs[values.index].premiumAmountBid;
  const clientPremium = !isInverted
    ? values.adjustedQuote.legs[values.index].premiumAmountOffer
    : values.adjustedQuote.legs[values.index].premiumAmountBid;
  const margin = values.adjustedQuote.legs[values.index]['marginAmount'] || 0;
  const premiumValues = nonVolBasedPremiumValues(
    {
      tradePremium: premiumValue,
      margin: margin,
      clientPrem: clientPremium
    },
    values.currencyPair,
    values.originalQuote.premiumCurrency,
    values.tradeBookingPremiumCurrency,
    values.tradeBookingPremiumStyle,
    values.referenceNotional,
    values.referenceCounterNotional,
    values.legConversionRate,
    values.forwardPointsMultiplier
  );

  return (
    premiumValues || {
      premiumValue,
      clientPremium,
      margin,
      cashPremium: null
    }
  );
}

export function nonVolBasedPremiumValues(
  currentValues: { tradePremium: number; margin: number; clientPrem: number },
  currencyPair: { base: string; term: string },
  premiumCurrency: string,
  tradeBookingPremiumCurrency: string,
  premiumStyle: string,
  baseAmount: number,
  termAmount: number,
  legConversionRate: number,
  forwardPointsMultiplier: number
): NonVolBasedPremiumValues {
  if (!premiumStyle) {
    return null;
  }
  if (premiumCurrency === currencyPair.base) {
    if (tradeBookingPremiumCurrency === currencyPair.base) {
      return nonVolBasedPremiumValuesCase1(
        currentValues,
        currencyPair,
        premiumCurrency,
        tradeBookingPremiumCurrency,
        premiumStyle,
        baseAmount,
        termAmount,
        forwardPointsMultiplier
      );
    } else {
      return nonVolBasedPremiumValuesCase2(
        currentValues,
        currencyPair,
        premiumCurrency,
        tradeBookingPremiumCurrency,
        premiumStyle,
        baseAmount,
        termAmount,
        legConversionRate,
        forwardPointsMultiplier
      );
    }
  } else {
    if (tradeBookingPremiumCurrency === currencyPair.term) {
      return nonVolBasedPremiumValuesCase3(
        currentValues,
        currencyPair,
        premiumCurrency,
        tradeBookingPremiumCurrency,
        premiumStyle,
        baseAmount,
        termAmount,
        forwardPointsMultiplier
      );
    } else {
      return nonVolBasedPremiumValuesCase4(
        currentValues,
        currencyPair,
        premiumCurrency,
        tradeBookingPremiumCurrency,
        premiumStyle,
        baseAmount,
        termAmount,
        legConversionRate,
        forwardPointsMultiplier
      );
    }
  }
}

export function nonVolBasedCashPremium(
  clientPremium: number,
  currencyPair: { base: string; term: string },
  premiumCurrency: string,
  tradeBookingPremiumCurrency: string,
  premiumStyle: string,
  baseAmount: number,
  termAmount: number,
  forwardPointsMultiplier: number
): number {
  const clientPremiumFormatted = +clientPremium.toFixed(
    premiumStyle === 'percent' ? 4 : 2
  );
  if (premiumCurrency === currencyPair.base) {
    if (tradeBookingPremiumCurrency === currencyPair.base) {
      if (premiumStyle === 'percent') {
        return (clientPremiumFormatted * baseAmount) / 100;
      }
    } else {
      if (premiumStyle === 'percent') {
        return (clientPremiumFormatted * termAmount) / 100;
      }
      if (premiumStyle === 'pips') {
        return (clientPremiumFormatted * baseAmount) / forwardPointsMultiplier;
      }
    }
  } else {
    if (tradeBookingPremiumCurrency === currencyPair.term) {
      if (premiumStyle === 'percent') {
        return (clientPremiumFormatted * termAmount) / 100;
      }

      if (premiumStyle === 'pips') {
        return (clientPremiumFormatted * baseAmount) / forwardPointsMultiplier;
      }
    } else {
      if (premiumStyle === 'percent') {
        return (clientPremiumFormatted * baseAmount) / 100;
      }
    }
  }
  return null;
}

function nonVolBasedPremiumValuesCase1(
  currentValues: { tradePremium: number; margin: number; clientPrem: number },
  currencyPair: { base: string; term: string },
  premiumCurrency: string,
  tradeBookingPremiumCurrency: string,
  premiumStyle: string,
  baseAmount: number,
  termAmount: number,
  forwardPointsMultiplier: number
): NonVolBasedPremiumValues {
  // Case 1: premiumCurrency is base currency and tradeBookingPremiumCurrency is also the base currency
  if (premiumStyle === 'Amount') {
    return null;
  }

  if (premiumStyle === 'percent') {
    const premiumValue = (currentValues.tradePremium / baseAmount) * 100;
    const margin = (currentValues.margin / baseAmount) * 100;
    const clientPremium = (currentValues.clientPrem / baseAmount) * 100;
    const cashPremium = nonVolBasedCashPremium(
      clientPremium,
      currencyPair,
      premiumCurrency,
      tradeBookingPremiumCurrency,
      premiumStyle,
      baseAmount,
      termAmount,
      forwardPointsMultiplier
    );

    return {
      premiumValue,
      margin,
      clientPremium,
      cashPremium
    };
  }
  return null;
}

function nonVolBasedPremiumValuesCase2(
  currentValues: { tradePremium: number; margin: number; clientPrem: number },
  currencyPair: { base: string; term: string },
  premiumCurrency: string,
  tradeBookingPremiumCurrency: string,
  premiumStyle: string,
  baseAmount: number,
  termAmount: number,
  legConversionRate: number,
  forwardPointsMultiplier: number
): NonVolBasedPremiumValues {
  // Case 2: premiumCurrency is base currency and tradeBookingPremiumCurrency is also the term currency
  if (premiumStyle === 'Amount') {
    const premiumValue = currentValues.tradePremium * legConversionRate;
    const margin = currentValues.margin * legConversionRate;
    const clientPremium = currentValues.clientPrem * legConversionRate;
    return {
      premiumValue,
      margin,
      clientPremium
    };
  }

  if (premiumStyle === 'percent') {
    const premiumValue =
      ((currentValues.tradePremium * legConversionRate) / termAmount) * 100;
    const margin =
      ((currentValues.margin * legConversionRate) / termAmount) * 100;
    const clientPremium =
      ((currentValues.clientPrem * legConversionRate) / termAmount) * 100;
    const cashPremium = nonVolBasedCashPremium(
      clientPremium,
      currencyPair,
      premiumCurrency,
      tradeBookingPremiumCurrency,
      premiumStyle,
      baseAmount,
      termAmount,
      forwardPointsMultiplier
    );
    return {
      premiumValue,
      margin,
      clientPremium,
      cashPremium
    };
  }

  if (premiumStyle === 'pips') {
    const premiumValue =
      ((currentValues.tradePremium * legConversionRate) / baseAmount) *
      forwardPointsMultiplier;
    const margin =
      ((currentValues.margin * legConversionRate) / baseAmount) *
      forwardPointsMultiplier;
    const clientPremium =
      ((currentValues.clientPrem * legConversionRate) / baseAmount) *
      forwardPointsMultiplier;
    const cashPremium = nonVolBasedCashPremium(
      clientPremium,
      currencyPair,
      premiumCurrency,
      tradeBookingPremiumCurrency,
      premiumStyle,
      baseAmount,
      termAmount,
      forwardPointsMultiplier
    );
    return {
      premiumValue,
      margin,
      clientPremium,
      cashPremium
    };
  }

  return null;
}

function nonVolBasedPremiumValuesCase3(
  currentValues: { tradePremium: number; margin: number; clientPrem: number },
  currencyPair: { base: string; term: string },
  premiumCurrency: string,
  tradeBookingPremiumCurrency: string,
  premiumStyle: string,
  baseAmount: number,
  termAmount: number,
  forwardPointsMultiplier: number
): NonVolBasedPremiumValues {
  // Case 3: premiumCurrency is term currency and tradeBookingPremiumCurrency is also the term currency
  if (premiumStyle === 'Amount') {
    return null;
  }

  if (premiumStyle === 'percent') {
    const premiumValue = (currentValues.tradePremium / termAmount) * 100;
    const margin = (currentValues.margin / termAmount) * 100;
    const clientPremium = (currentValues.clientPrem / termAmount) * 100;
    const cashPremium = nonVolBasedCashPremium(
      clientPremium,
      currencyPair,
      premiumCurrency,
      tradeBookingPremiumCurrency,
      premiumStyle,
      baseAmount,
      termAmount,
      forwardPointsMultiplier
    );
    return {
      premiumValue,
      margin,
      clientPremium,
      cashPremium
    };
  }

  if (premiumStyle === 'pips') {
    const premiumValue =
      (currentValues.tradePremium / baseAmount) * forwardPointsMultiplier;
    const margin =
      (currentValues.margin / baseAmount) * forwardPointsMultiplier;
    const clientPremium =
      (currentValues.clientPrem / baseAmount) * forwardPointsMultiplier;
    const cashPremium = nonVolBasedCashPremium(
      clientPremium,
      currencyPair,
      premiumCurrency,
      tradeBookingPremiumCurrency,
      premiumStyle,
      baseAmount,
      termAmount,
      forwardPointsMultiplier
    );
    return {
      premiumValue,
      margin,
      clientPremium,
      cashPremium
    };
  }

  return null;
}

function nonVolBasedPremiumValuesCase4(
  currentValues: { tradePremium: number; margin: number; clientPrem: number },
  currencyPair: { base: string; term: string },
  premiumCurrency: string,
  tradeBookingPremiumCurrency: string,
  premiumStyle: string,
  baseAmount: number,
  termAmount: number,
  legConversionRate: number,
  forwardPointsMultiplier: number
): NonVolBasedPremiumValues {
  // Case 4: premiumCurrency is term currency and tradeBookingPremiumCurrency is also the base currency
  if (premiumStyle === 'Amount') {
    const premiumValue = currentValues.tradePremium / legConversionRate;
    const margin = currentValues.margin / legConversionRate;
    const clientPremium = currentValues.clientPrem / legConversionRate / 100;
    return {
      premiumValue,
      margin,
      clientPremium
    };
  }

  if (premiumStyle === 'percent') {
    const premiumValue =
      (currentValues.tradePremium / legConversionRate / baseAmount) * 100;

    const margin =
      (currentValues.margin / legConversionRate / baseAmount) * 100;
    const clientPremium =
      (currentValues.clientPrem / legConversionRate / baseAmount) * 100;
    const cashPremium = nonVolBasedCashPremium(
      clientPremium,
      currencyPair,
      premiumCurrency,
      tradeBookingPremiumCurrency,
      premiumStyle,
      baseAmount,
      termAmount,
      forwardPointsMultiplier
    );

    return {
      premiumValue,
      margin,
      clientPremium,
      cashPremium
    };
  }
  return null;
}

export function getClientPremium(
  premium: number,
  margin: number,
  dir: number,
  retrocessionFee: number
): number {
  return +premium + dir * (+margin + +retrocessionFee);
}

export function cleanBookedHedges(bookedHedges: FormArray): BookedHedge[] {
  return bookedHedges.controls.map(hedge => {
    return {
      hedgeAmount: cleanFormattedNumeric(hedge.get('hedgeAmount')?.value),
      hedgeRate: +hedge.get('hedgeRate').value,
      hedgeDeliveryDate: hedge.get('hedgeDeliveryDate')?.value
    };
  });
}
